import { inject, Injectable } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { DsModalConfig } from '@design-system/ds-base-modal/interfaces/ds-modal-config.interface';

/**
 * Design system modal service
 *
 * Provides a standardized way to open modals with design system styling.
 * Use this service to ensure consistent modal appearance across the application.
 *
 * @example
 * ```typescript
 * constructor(private dsModal: DsModalService) {}
 *
 * openDeleteModal(): void {
 *   const dialogRef = this.dsModal.open(DeleteConfirmComponent, {
 *     data: { itemId: 123 },
 *     width: '400px'
 *   });
 *
 *   dialogRef.afterClosed().subscribe(result => {
 *     if (result) {
 *       // Handle confirmation
 *     }
 *   });
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DsModalService {
  private readonly matDialog = inject(MatDialog);

  private readonly defaultPanelClass = 'ds-modal-panel';

  /**
   * Opens a modal dialog with design system styling
   *
   * @param component - The component to render inside the modal
   * @param config - Optional configuration for the modal
   * @returns Reference to the opened dialog
   */
  open<T, D = unknown, R = unknown>(component: ComponentType<T>, config?: DsModalConfig<D>): MatDialogRef<T, R> {
    const panelClass = this.buildPanelClass(config?.panelClass);

    const dialogConfig: MatDialogConfig<D> = {
      autoFocus: false,
      ...config,
      panelClass,
    };

    return this.matDialog.open<T, D, R>(component, dialogConfig);
  }

  /**
   * Builds the panel class array, ensuring the default DS class is always included
   */
  private buildPanelClass(additionalClasses?: string | string[]): string[] {
    const classes = [this.defaultPanelClass];

    if (additionalClasses) {
      if (Array.isArray(additionalClasses)) {
        classes.push(...additionalClasses);
      } else {
        classes.push(additionalClasses);
      }
    }

    return classes;
  }
}
